import { useState } from 'react';
import { History, MicVocal, Upload } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils/cn';
import { SynthesizePanel } from './SynthesizePanel';
import { TtsHistoryPanel } from './TtsHistoryPanel';
import { UploadAudioPanel } from './UploadAudioPanel';

type Tab = 'upload' | 'synthesize' | 'history';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTab?: Tab;
}

const TABS: { id: Tab; label: string; description: string; icon: React.ComponentType<{ className?: string }> }[] = [
  {
    id: 'upload',
    label: 'Upload',
    description: 'Audio file',
    icon: Upload,
  },
  {
    id: 'synthesize',
    label: 'Synthesize',
    description: 'From text',
    icon: MicVocal,
  },
  {
    id: 'history',
    label: 'History',
    description: 'Previous renders',
    icon: History,
  },
];

export function AddPromptSheet({
  open,
  onOpenChange,
  defaultTab = 'upload',
}: Props) {
  const [tab, setTab] = useState<Tab>(defaultTab);

  const close = () => onOpenChange(false);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>Add Voice File</SheetTitle>
          <SheetDescription>
            Bring audio into the library — upload an existing file, synthesize
            one from text, or resume a previous TTS render.
          </SheetDescription>
        </SheetHeader>

        {/* Segmented tab bar */}
        <div className="mt-4 grid grid-cols-3 gap-1 rounded-lg border border-border bg-muted/30 p-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={cn(
                  'flex flex-col items-center gap-0.5 rounded-md px-2 py-2 text-center transition-all',
                  active
                    ? 'bg-card shadow-sm ring-1 ring-border'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <div className="flex items-center gap-1.5">
                  <Icon
                    className={cn(
                      'h-3.5 w-3.5',
                      active ? 'text-primary' : 'text-muted-foreground',
                    )}
                  />
                  <span className="text-xs font-semibold">{t.label}</span>
                </div>
                <span className="text-[10px] text-muted-foreground">
                  {t.description}
                </span>
              </button>
            );
          })}
        </div>

        <div className="mt-4">
          {tab === 'upload' && <UploadAudioPanel onSuccess={close} />}
          {tab === 'synthesize' && <SynthesizePanel onSuccess={close} />}
          {tab === 'history' && <TtsHistoryPanel onSuccess={close} />}
        </div>
      </SheetContent>
    </Sheet>
  );
}
